import type { Ring, Vec2 } from '../../geo/types';
import { distancePointSegment, pointInRing } from '../../geo/polygon';
import type { Lot } from './types';
import { settingOf } from './houseSetting';
import type { StreetMap } from './houseStreets';
import { frontsOnSide, readSidesOf, type ReadSide, type Span } from './lastageSides';
import { WOODCUT_READINGS } from './woodcut/woodcutGables';

/** A building the painting shows in the Lastage: its foot in the painter frame's pixels and on the ground. */
export interface PaintedBuilding {
  readonly frame: Vec2;
  readonly at: Vec2;
}

export interface PaintedLastage {
  /** How far from a painted building's ground point the house kept for it may stand, in metres. */
  readonly reach: number;
  /** The parts of the Lastage read against the painting, in layout metres. */
  readonly areas: readonly { readonly id: string; readonly ring: Ring }[];
  readonly buildings: readonly PaintedBuilding[];
}

/**
 * PLAN Lots, the Lastage by the painting (2026-09-28). Cornelis Anthonisz paints the Lastage in 1538
 * as fields, ditches, the rope walks and a few buildings: a cluster by the timber yard outside the
 * Sint Antoniespoort, a house at the east end of the rope walks, houses and a shed along the dike road
 * and by its bridge, a hut by the tenter field. The feet of these buildings were read on the painting
 * (docs/reference/painting-1538-amsterdam-museum.jpg, in the 2000 px painter frame) and cast onto the
 * ground through the painter camera and the inverse residual warp (tools/layout/painterframe.py,
 * painted_to_ground). The warp fits its points to 17.7 px RMS, and a painter-frame pixel spans
 * 1.0-1.3 m of ground here, so `reach` is 20 m. The Geldersekade spit is not read here: its timber
 * yard waits for the layout's record of it.
 */
export const PAINTED_LASTAGE: PaintedLastage = {
  reach: 20,
  areas: [
    {
      id: 'kloveniersburgwal-south',
      ring: [
        [220, -610],
        [560, -610],
        [560, -360],
        [220, -360],
      ],
    },
    {
      id: 'sint-antoniesbreestraat',
      ring: [
        [380, -360],
        [560, -360],
        [560, -95],
        [380, -95],
      ],
    },
    {
      id: 'boomssloot',
      ring: [
        [560, -360],
        [910, -360],
        [910, 55],
        [530, 55],
        [530, -95],
        [560, -95],
      ],
    },
  ],
  buildings: [
    { frame: [488, 926], at: [595.9, -130.0] },
    { frame: [499, 931], at: [594.5, -116.6] },
    { frame: [519, 922], at: [574.2, -105.1] },
    { frame: [532, 925], at: [570.0, -91.6] },
    { frame: [546, 930], at: [567.5, -75.8] },
    { frame: [502, 954], at: [615.1, -97.4] },
    { frame: [519, 940], at: [591.9, -92.4] },
    { frame: [541, 945], at: [584.6, -69.5] },
    { frame: [544, 989], at: [623.4, -35.3] },
    { frame: [370.5, 799], at: [532.0, -336.5] },
    { frame: [372.6, 830], at: [566.8, -311.3] },
    { frame: [364, 829], at: [571.8, -320.9] },
    { frame: [432.5, 787], at: [474.7, -280.5] },
    { frame: [442, 826], at: [515.2, -244.0] },
    { frame: [480.5, 831], at: [497.6, -203.3] },
    { frame: [513.5, 855], at: [506.4, -156.3] },
    { frame: [406.8, 689], at: [367.4, -375.8] },
  ],
};

function distanceToLot(p: Vec2, lot: Lot): number {
  if (pointInRing(p, lot.corners)) return 0;
  return Math.min(
    ...lot.corners.map((a, i) => distancePointSegment(p, { a, b: lot.corners[(i + 1) % 4]! }))
  );
}

interface Pair {
  readonly building: number;
  readonly lot: number;
  readonly distance: number;
}

/**
 * Each painted building's house among `candidates`: the nearest pairs are matched first, so a house
 * between two painted buildings goes to the nearer; each kept house maps to its distance.
 */
export function paintedMatches(
  candidates: readonly Lot[],
  painted: PaintedLastage
): ReadonlyMap<Lot, number> {
  const pairs: Pair[] = painted.buildings.flatMap((b, building) =>
    candidates
      .map((lot, i) => ({ building, lot: i, distance: distanceToLot(b.at, lot) }))
      .filter((pair) => pair.distance <= painted.reach)
  );
  pairs.sort((x, y) => x.distance - y.distance || x.building - y.building || x.lot - y.lot);
  const given = new Set<number>();
  const kept = new Map<Lot, number>();
  for (const pair of pairs) {
    const lot = candidates[pair.lot]!;
    if (given.has(pair.building) || kept.has(lot)) continue;
    given.add(pair.building);
    kept.set(lot, pair.distance);
  }
  return kept;
}

/**
 * A block side the woodcut reads keeps as many houses as it draws fronts (`lastageSides.ts`),
 * including any part of that side outside the painting's read areas. Elsewhere in a read area,
 * a Lastage house stands only where it is the nearest free house to a painted building, one house
 * to a building.
 */
export function keepPaintedLastage(
  lots: readonly Lot[],
  painted: PaintedLastage = PAINTED_LASTAGE,
  sides: readonly ReadSide[] = []
): readonly Lot[] {
  const read = (lot: Lot) =>
    settingOf(lot.sourceId) === 'lastage' &&
    painted.areas.some(({ ring }) => pointInRing(lot.obb.center, ring));
  const matched = paintedMatches(lots.filter(read), painted);
  const onSides = new Set(sides.flatMap((side) => side.lots));
  const kept = new Set([
    ...[...matched.keys()].filter((lot) => !onSides.has(lot)),
    ...sides.flatMap((side) => frontsOnSide(side.lots, side.fronts, matched)),
  ]);
  return lots.filter((lot) => (onSides.has(lot) ? kept.has(lot) : !read(lot) || kept.has(lot)));
}

/** The Lastage lots thinned, and the stretch of each woodcut-read side, which the thinning leaves in place. */
export interface ThinnedLastage {
  readonly lots: readonly Lot[];
  readonly sides: ReadonlyMap<string, Span>;
}

export function thinLastage(
  lots: readonly Lot[],
  map: StreetMap,
  readings: Readonly<Record<string, { readonly fronts: number }>> = WOODCUT_READINGS.sides
): ThinnedLastage {
  const lastage = lots.filter((lot) => settingOf(lot.sourceId) === 'lastage');
  const sides = readSidesOf(lastage, map, readings);
  return {
    lots: keepPaintedLastage(lots, PAINTED_LASTAGE, sides),
    sides: new Map(sides.map((side) => [side.key, side.span])),
  };
}
